import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Package,
  ArrowLeft,
  Search,
  Loader2,
  Edit,
  Trash2,
  Plus,
  X,
  Save,
  GripVertical,
  Image as ImageIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const departments = [
  { value: 'rental', label: 'Location' },
  { value: 'sales', label: 'Ventes' },
  { value: 'parts', label: 'Pièces' },
  { value: 'software', label: 'Logiciels' },
  { value: 'service', label: 'Services' }
];

const conditions = [
  { value: 'new', label: 'Neuf' },
  { value: 'used', label: 'Usagé' }
];

const emptyProduct = {
  name_fr: '',
  name_en: '',
  description_fr: '',
  description_en: '',
  department: 'rental',
  category: '',
  price: 0,
  rental_price_daily: null,
  rental_price_weekly: null,
  rental_price_monthly: null,
  condition: 'new',
  stock_quantity: 0,
  images: [],
  specifications: {},
  is_available: true,
  display_order: 0,
  serial_number: '',
  sku: ''
};

export default function AdminProducts() {
  const { token, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin()) {
      navigate('/');
      return;
    }
    fetchProducts();
  }, [isAuthenticated, authLoading, department, search]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      if (department) params.append('department', department);
      if (search) params.append('search', search);
      params.append('available_only', 'false');
      
      const response = await axios.get(`${API}/products?${params}`, { headers });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setFormData(emptyProduct);
    setIsModalOpen(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      ...emptyProduct,
      ...product,
      rental_price_daily: product.rental_price_daily || '',
      rental_price_weekly: product.rental_price_weekly || '',
      rental_price_monthly: product.rental_price_monthly || '',
      serial_number: product.serial_number || '',
      sku: product.sku || ''
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Prepare data
      const dataToSend = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        display_order: parseInt(formData.display_order) || 0,
        rental_price_daily: formData.rental_price_daily ? parseFloat(formData.rental_price_daily) : null,
        rental_price_weekly: formData.rental_price_weekly ? parseFloat(formData.rental_price_weekly) : null,
        rental_price_monthly: formData.rental_price_monthly ? parseFloat(formData.rental_price_monthly) : null,
      };
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, dataToSend, { headers });
        toast.success('Produit modifié avec succès');
      } else {
        await axios.post(`${API}/products`, dataToSend, { headers });
        toast.success('Produit créé avec succès');
      }
      
      setIsModalOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/products/${productId}`, { headers });
      toast.success('Produit supprimé');
      setDeleteConfirm(null);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(price);
  };

  const getDepartmentLabel = (dept) => {
    const found = departments.find(d => d.value === dept);
    return found ? found.label : dept;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin()) return null;

  return (
    <div className="min-h-screen py-8 bg-gray-50" data-testid="admin-products">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            <Link to="/admin" className="inline-flex items-center text-gray-500 hover:text-black mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au tableau de bord
            </Link>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                  <Package className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-black">Gestion des Produits</h1>
                  <p className="text-gray-500">{products.length} produits</p>
                </div>
              </div>
              <Button 
                onClick={handleCreate}
                className="bg-black text-white hover:bg-gray-800"
                data-testid="add-product-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouveau produit
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white border-gray-300"
                data-testid="search-input"
              />
            </div>
            <Select value={department || "all"} onValueChange={(v) => setDepartment(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 bg-white border-gray-300" data-testid="department-filter">
                <SelectValue placeholder="Tous les départements" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">Tous</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Products List */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-16 bg-gray-100 animate-pulse rounded" />
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="py-12 text-center">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun produit trouvé</p>
                  <Button 
                    onClick={handleCreate}
                    variant="outline"
                    className="mt-4 border-gray-300"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un produit
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {products.map((product) => (
                    <div 
                      key={product.id} 
                      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                      data-testid={`product-${product.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-gray-400 cursor-grab">
                          <GripVertical className="h-5 w-5" />
                        </div>
                        <span className="text-gray-400 text-sm font-mono w-8">
                          #{product.display_order || 0}
                        </span>
                        <img
                          src={product.images?.[0] || 'https://via.placeholder.com/64?text=No+Image'}
                          alt={product.name_fr}
                          className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                        />
                        <div>
                          <p className="font-medium text-black">{product.name_fr}</p>
                          <p className="text-gray-500 text-sm">{product.category}</p>
                          {product.sku && (
                            <p className="text-gray-400 text-xs font-mono">SKU: {product.sku}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge 
                          className={`${product.is_available ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'} border`}
                        >
                          {product.is_available ? 'Disponible' : 'Indisponible'}
                        </Badge>
                        <Badge className="bg-gray-100 text-gray-700 border border-gray-200">
                          {getDepartmentLabel(product.department)}
                        </Badge>
                        <p className="font-mono text-black w-24 text-right">{formatPrice(product.price)}</p>
                        <p className="text-gray-500 text-sm w-20">Stock: {product.stock_quantity}</p>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-gray-500 hover:text-black"
                            onClick={() => handleEdit(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-gray-500 hover:text-red-600"
                            onClick={() => setDeleteConfirm(product)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Edit/Create Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-black">
              {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Nom (Français) *</Label>
                <Input
                  value={formData.name_fr}
                  onChange={(e) => setFormData({...formData, name_fr: e.target.value})}
                  className="border-gray-300"
                  data-testid="product-name-fr"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Nom (Anglais)</Label>
                <Input
                  value={formData.name_en}
                  onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                  className="border-gray-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Description (Français)</Label>
                <Textarea
                  value={formData.description_fr}
                  onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
                  className="border-gray-300"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Description (Anglais)</Label>
                <Textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                  className="border-gray-300"
                  rows={3}
                />
              </div>
            </div>

            {/* Department & Category */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Département *</Label>
                <Select 
                  value={formData.department} 
                  onValueChange={(v) => setFormData({...formData, department: v})}
                >
                  <SelectTrigger className="border-gray-300" data-testid="product-department">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {departments.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Catégorie *</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="border-gray-300"
                  placeholder="Ex: borne, televiseur..."
                  data-testid="product-category"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Condition</Label>
                <Select 
                  value={formData.condition} 
                  onValueChange={(v) => setFormData({...formData, condition: v})}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {conditions.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Prix ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  className="border-gray-300"
                  data-testid="product-price"
                />
              </div>
              {formData.department === 'rental' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Prix/jour</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.rental_price_daily}
                      onChange={(e) => setFormData({...formData, rental_price_daily: e.target.value})}
                      className="border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Prix/semaine</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.rental_price_weekly}
                      onChange={(e) => setFormData({...formData, rental_price_weekly: e.target.value})}
                      className="border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Prix/mois</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.rental_price_monthly}
                      onChange={(e) => setFormData({...formData, rental_price_monthly: e.target.value})}
                      className="border-gray-300"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Stock & Display */}
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Stock</Label>
                <Input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                  className="border-gray-300"
                  data-testid="product-stock"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: e.target.value})}
                  className="border-gray-300"
                  data-testid="product-display-order"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">SKU</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({...formData, sku: e.target.value})}
                  className="border-gray-300"
                  placeholder="Code produit"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">N° Série</Label>
                <Input
                  value={formData.serial_number}
                  onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                  className="border-gray-300"
                />
              </div>
            </div>

            {/* Images */}
            <div className="space-y-2">
              <Label className="text-gray-700">Images (URLs séparées par des virgules)</Label>
              <Textarea
                value={formData.images?.join(', ') || ''}
                onChange={(e) => setFormData({
                  ...formData, 
                  images: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                })}
                className="border-gray-300"
                rows={2}
                placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
              />
              {formData.images?.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {formData.images.map((img, idx) => (
                    <img 
                      key={idx} 
                      src={img} 
                      alt={`Preview ${idx}`}
                      className="w-16 h-16 object-cover rounded border border-gray-200"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Availability Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_available"
                checked={formData.is_available}
                onChange={(e) => setFormData({...formData, is_available: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="is_available" className="text-gray-700 cursor-pointer">
                Produit disponible à la vente/location
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsModalOpen(false)}
              className="border-gray-300"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving || !formData.name_fr || !formData.category}
              className="bg-black text-white hover:bg-gray-800"
              data-testid="save-product-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {editingProduct ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-black">Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 py-4">
            Êtes-vous sûr de vouloir supprimer <strong>{deleteConfirm?.name_fr}</strong>? 
            Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm(null)}
              className="border-gray-300"
            >
              Annuler
            </Button>
            <Button 
              onClick={() => handleDelete(deleteConfirm.id)}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
